import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { 
  layoutModeAtom, 
  sidebarCollapsedAtom, 
  chatSidebarCollapsedAtom 
} from '../atoms/v3/uiAtoms';
import { 
  voiceModeAtom, 
  isVoiceRecordingAtom 
} from '../atoms/v3/chatInputAtoms';
import { taskV2FocusedIndexAtom } from '../atoms/v3/taskV2Atoms';
import { globalKeymapModalOpenAtom } from '../atoms/v3/uiAtoms';
import { isTouchDevice } from '../utils/platformUtils';

/**
 * 全局快捷键业务逻辑 Hook
 * 提供所有全局快捷键的具体实现函数
 */
export const useGlobalShortcutActions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const layoutMode = useAtomValue(layoutModeAtom);
  const [sidebarCollapsed, setSidebarCollapsed] = useAtom(sidebarCollapsedAtom);
  const [chatSidebarCollapsed, setChatSidebarCollapsed] = useAtom(chatSidebarCollapsedAtom);
  const [voiceMode, setVoiceMode] = useAtom(voiceModeAtom);
  const [isVoiceRecording] = useAtom(isVoiceRecordingAtom);
  const setTaskV2FocusedIndex = useSetAtom(taskV2FocusedIndexAtom);
  const [isKeymapModalOpen, setIsKeymapModalOpen] = useAtom(globalKeymapModalOpenAtom);

  // 获取当前页面类型
  const getCurrentPageType = useCallback((): string => {
    const path = location.pathname;
    if (path.includes('/v3/taskv2')) return 'taskv2';
    if (path.includes('/v3/chat')) return 'chat';
    if (path.includes('/v3/me')) return 'me';
    return 'unknown';
  }, [location.pathname]);

  // 聚焦到桌面端任务列表的函数
  const focusDesktopTaskList = useCallback(() => {
    const selectors = [
      // 桌面端大纲区域
      '[data-task-list]',
      '.desktop-outline',
      '.task-list',
      // 任务列表中的可交互元素
      '.desktop-outline input',
      '.desktop-outline button',
      '.task-list input',
      '.task-list button',
      // 通用选择器
      '.desktop-sidebar [tabindex]',
      '.desktop-sidebar button'
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector) as HTMLElement;
      if (element && element.offsetParent !== null) {
        element.focus();
        console.log(`Desktop task list focused using selector: ${selector}`);
        return true;
      }
    }

    console.warn('Failed to focus desktop task list');
    return false;
  }, []);

  // 聚焦到桌面端聊天输入框的函数
  const focusDesktopChatInput = useCallback(() => {
    const selectors = [
      // 桌面端聊天侧边栏特定选择器
      '[data-desktop-chat-input]',
      '.desktop-chat-sidebar textarea',
      '.desktop-chat-sidebar input',
      // V3 Chat 页面的主要输入框
      'textarea[placeholder*="帮我规划"]',
      'textarea[placeholder*="日程"]',
      // 通用聊天输入框
      'textarea[placeholder*="消息"]',
      'input[placeholder*="消息"]',
      'textarea[placeholder*="Message"]',
      'input[placeholder*="Message"]'
    ];

    for (const selector of selectors) {
      const chatInput = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
      if (chatInput && chatInput.offsetParent !== null) {
        chatInput.focus();
        console.log(`Desktop chat input focused using selector: ${selector}`);
        return true;
      }
    }

    console.warn('Failed to focus desktop chat input');
    return false;
  }, []);

  // 聚焦到 Chat 输入框的函数（兼容移动端和桌面端）
  const focusChatInput = useCallback(() => {
    // 多次尝试聚焦，确保页面加载完成
    const tryFocus = (attempt = 0) => {
      const maxAttempts = 10;

      // 尝试多种选择器，按优先级排序
      const selectors = [
        // V3 Chat 页面的主要输入框
        'textarea[placeholder*="帮我规划"]',
        'textarea[placeholder*="日程"]',
        // 通用聊天输入框
        'textarea[placeholder*="消息"]',
        'input[placeholder*="消息"]',
        'textarea[placeholder*="Message"]',
        'input[placeholder*="Message"]',
        // 带有特定属性的输入框
        'textarea[data-chat-input]',
        'input[data-chat-input]',
        // CSS 类选择器
        '.chat-input textarea',
        '.chat-input input',
        // 通用选择器（最后尝试）
        'textarea[rows]',
        'input[type="text"]'
      ];

      for (const selector of selectors) {
        const chatInput = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
        if (chatInput && chatInput.offsetParent !== null) { // 确保元素可见
          chatInput.focus();
          console.log(`Chat input focused using selector: ${selector}`);
          return true;
        }
      }

      // 如果没找到，继续尝试
      if (attempt < maxAttempts) {
        setTimeout(() => tryFocus(attempt + 1), 200);
      } else {
        console.warn('Failed to focus chat input after', maxAttempts, 'attempts');
      }

      return false;
    };

    // 立即尝试一次，然后延迟尝试
    if (!tryFocus()) {
      setTimeout(() => tryFocus(), 100);
    }
  }, []);

  // 智能 Chat 切换函数（布局感知）
  const smartChatSwitch = useCallback(() => {
    console.log('Smart chat switch triggered');
    
    // 如果正在录音，先停止录音
    if (isVoiceRecording) {
      window.dispatchEvent(new CustomEvent('cancelVoiceRecording'));
    }

    // 如果在语音模式，切换回文本模式
    if (voiceMode) {
      setVoiceMode(false);
    }

    // 清除 TaskV2 的焦点状态
    setTaskV2FocusedIndex(-1);

    if (layoutMode === 'desktop') {
      // 桌面端：聚焦到右栏聊天输入，如果右栏折叠则先展开
      if (chatSidebarCollapsed) {
        setChatSidebarCollapsed(false);
      }
      // 延迟聚焦确保侧边栏展开完成
      setTimeout(() => {
        focusDesktopChatInput();
      }, 100);
    } else {
      // 移动端：保持原有的页面导航逻辑
      const currentPageType = getCurrentPageType();
      if (currentPageType === 'chat') {
        focusChatInput();
      } else {
        navigate('/v3/chat');
        focusChatInput();
      }
    }
  }, [layoutMode, chatSidebarCollapsed, setChatSidebarCollapsed, focusDesktopChatInput,
      getCurrentPageType, focusChatInput, navigate, isVoiceRecording, voiceMode, setVoiceMode, setTaskV2FocusedIndex]);

  // 智能任务切换函数（布局感知）- 修复了桌面端页面切换问题
  const smartTaskSwitch = useCallback(() => {
    console.log('Smart task switch triggered');
    
    if (layoutMode === 'desktop') {
      // 桌面端：先检查当前页面，如果不是 taskv2 则先导航
      const currentPageType = getCurrentPageType();
      if (currentPageType !== 'taskv2') {
        // 先导航到 taskv2 页面
        navigate('/v3/taskv2');
        // 延迟聚焦确保页面加载完成
        setTimeout(() => {
          if (sidebarCollapsed) {
            setSidebarCollapsed(false);
          }
          setTimeout(() => {
            focusDesktopTaskList();
          }, 100);
        }, 100);
      } else {
        // 已经在 taskv2 页面，直接聚焦到左栏任务列表
        if (sidebarCollapsed) {
          setSidebarCollapsed(false);
        }
        // 延迟聚焦确保侧边栏展开完成
        setTimeout(() => {
          focusDesktopTaskList();
        }, 100);
      }
    } else {
      // 移动端：导航到规划页面
      navigate('/v3/taskv2');
    }
  }, [layoutMode, sidebarCollapsed, setSidebarCollapsed, focusDesktopTaskList, navigate, getCurrentPageType]);

  // 切换左侧边栏
  const toggleLeftSidebar = useCallback(() => {
    console.log('Toggle left sidebar triggered');
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed, setSidebarCollapsed]);

  // 切换右侧边栏
  const toggleRightSidebar = useCallback(() => {
    console.log('Toggle right sidebar triggered');
    setChatSidebarCollapsed(!chatSidebarCollapsed);
  }, [chatSidebarCollapsed, setChatSidebarCollapsed]);

  // 关闭模态框
  const closeModal = useCallback(() => {
    console.log('Close modal triggered');
    if (isKeymapModalOpen) {
      setIsKeymapModalOpen(false);
    }
  }, [isKeymapModalOpen, setIsKeymapModalOpen]);

  return useMemo(() => ({
    smartChatSwitch,
    smartTaskSwitch,
    toggleLeftSidebar,
    toggleRightSidebar,
    closeModal,
    getCurrentPageType,
    focusDesktopTaskList,
    focusDesktopChatInput,
    focusChatInput
  }), [
    smartChatSwitch,
    smartTaskSwitch,
    toggleLeftSidebar,
    toggleRightSidebar,
    closeModal,
    getCurrentPageType,
    focusDesktopTaskList,
    focusDesktopChatInput,
    focusChatInput
  ]);
};
